import { get, post, put, del } from '../httpMethods';
import { ApiResponse } from '../../types/api';
import { HospitalSetting } from '../../types/settings';

export interface BulkSettingItem {
  key: string;
  value: string;
  label?: string;
  valueType?: string;
  description?: string;
  group?: string;
}

export interface DetectedPrinter {
  name: string;
  shareName: string;
  portName: string;
  driverName: string;
  shared: boolean;
}

export interface DevicePayload {
  clientId?: string;
  name: string;
  type: string;
  shareName?: string;
  port?: string;
  pcName?: string;
  networkIp?: string;
  workstation?: string;
  enabled: boolean;
  isReceiptPrinter: boolean;
  isLabelPrinter: boolean;
  isDiagnosticReceiptPrinter?: boolean;
  isInvoicePrinter?: boolean;
  isDocumentPrinter?: boolean;
}

/** A print agent (socket client) enrolled with the backend. */
export interface PrintAgentInfo {
  id: string;
  hostname?: string;
  ipAddresses: string[];
  printers: Array<{ name?: string; shareName?: string; port?: string }>;
  agentVersion?: string;
  enabled: boolean;
  online: boolean;
  lastSeenAt?: string | null;
  enrolledAt?: string | null;
}

/** A print-job activity-log row (payload bytes are never included). */
export interface PrintJobLog {
  id: string;
  status: string;
  kind: string;
  format?: string;
  printerLabel?: string;
  shareName: string;
  targetHost?: string;
  targetDeviceId?: string;
  payloadBytes?: number;
  payloadSha256?: string;
  attempts: number;
  source?: string;
  requestedBy?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
  deliveredAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface PrintJobLogQuery {
  status?: string;
  source?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

/** Auto-delete settings for the print activity log. */
export interface PrintLogSettings {
  /** Delete logs older than this many days; 0 keeps them forever. */
  retentionDays: number;
}

export const settingsService = {
  getSettings: (): Promise<ApiResponse<HospitalSetting[]>> => {
    return get<HospitalSetting[]>('/system/settings');
  },

  /** Public, unauthenticated branding settings (favicon/logo/name). */
  getPublicSettings: (): Promise<ApiResponse<Record<string, string>>> => {
    return get<Record<string, string>>('/system/public/settings');
  },

  detectPrinters: (): Promise<ApiResponse<DetectedPrinter[]>> => {
    return get<DetectedPrinter[]>('/system/devices/printers');
  },

  listDevices: (): Promise<ApiResponse<DevicePayload[]>> => {
    return get<DevicePayload[]>('/system/devices');
  },

  upsertDevice: (device: DevicePayload): Promise<ApiResponse<DevicePayload>> => {
    return post<DevicePayload>('/system/devices', device);
  },

  deleteDevice: (clientId: string): Promise<ApiResponse<{ deleted: boolean }>> => {
    return del<{ deleted: boolean }>(`/system/devices/${clientId}`);
  },

  /** Dashboard origins each workstation allows on its local print agent. */
  getAllowedAgentOrigins: (): Promise<ApiResponse<{ origins: string[] }>> => {
    return get<{ origins: string[] }>('/system/devices/allowed-origins');
  },

  setAllowedAgentOrigins: (origins: string[]): Promise<ApiResponse<{ origins: string[] }>> => {
    return put<{ origins: string[] }>('/system/devices/allowed-origins', { origins });
  },

  /** Host identities whose dashboard is currently connected to receive prints. */
  listPrintClients: (): Promise<ApiResponse<{ identities: string[] }>> => {
    return get<{ identities: string[] }>('/system/print-jobs/clients');
  },

  /** Enrolled print agents (socket clients) and their online status. */
  listPrintAgents: (): Promise<ApiResponse<{ agents: PrintAgentInfo[] }>> => {
    return get<{ agents: PrintAgentInfo[] }>('/system/print-agents');
  },

  /** Paginated print-job activity log (audit/history of every print). */
  listPrintJobs: (params?: PrintJobLogQuery): Promise<ApiResponse<PrintJobLog[]>> => {
    return get<PrintJobLog[]>('/system/print-jobs', params);
  },

  /** Auto-delete retention settings for the print log. */
  getPrintLogSettings: (): Promise<ApiResponse<PrintLogSettings>> => {
    return get<PrintLogSettings>('/system/print-jobs/log-settings');
  },

  /** Save the auto-delete retention (days; 0 = keep forever). */
  updatePrintLogSettings: (retentionDays: number): Promise<ApiResponse<PrintLogSettings>> => {
    return put<PrintLogSettings>('/system/print-jobs/log-settings', { retentionDays });
  },

  /** Permanently delete every print-job log. */
  clearPrintJobs: (): Promise<ApiResponse<{ deleted: number }>> => {
    return del<{ deleted: number }>('/system/print-jobs');
  },

  /** Permanently delete selected print-job logs. */
  bulkDeletePrintJobs: (ids: string[]): Promise<ApiResponse<{ deleted: number }>> => {
    return post<{ deleted: number }>('/system/print-jobs/bulk-delete', { ids });
  },

  /** Re-deliver a print job from the log. */
  reprintPrintJob: (id: string): Promise<ApiResponse<{ id: string; delivered: boolean }>> => {
    return post<{ id: string; delivered: boolean }>(
      `/system/print-jobs/${encodeURIComponent(id)}/reprint`,
    );
  },

  /** Revoke a print agent (disable + invalidate its device token). */
  revokePrintAgent: (id: string): Promise<ApiResponse<{ revoked: boolean }>> => {
    return del<{ revoked: boolean }>(`/system/print-agents/${id}`);
  },

  /** Permanently remove a revoked print agent (it can enrol again afterwards). */
  removePrintAgent: (id: string): Promise<ApiResponse<{ removed: boolean }>> => {
    return del<{ removed: boolean }>(`/system/print-agents/${id}/permanent`);
  },

  updateSettings: (data: any): Promise<ApiResponse<HospitalSetting>> => {
    return put<HospitalSetting>('/system/settings', data);
  },

  bulkUpdateSettings: (settings: BulkSettingItem[]): Promise<ApiResponse<HospitalSetting[]>> => {
    return post<HospitalSetting[]>('/system/settings/bulk', { settings });
  },
};
